import { Component, OnInit, computed, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { DashboardService } from '@services/http/dashboard.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { AppState } from '@src/app/state';
import { MenuItem } from 'primeng/api';
import {
  DocumentAnalysisRow,
  DocumentAnalysisViewMode,
  buildTotals,
  flattenTimeline,
  matchesSearch,
} from './document-analysis.model';

@Component({
  selector: 'app-document-analysis',
  templateUrl: './document-analysis.component.html',
  styleUrls: ['./document-analysis.component.scss'],
  standalone: false,
})
export class DocumentAnalysisComponent extends VaultBaseComponent implements OnInit {
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: this.t(this.tk.General.Dashboard),
  };
  breadcrumbs: MenuItem[] = [];

  readonly companies = signal<DocumentAnalysisRow[]>([]);
  readonly search = signal('');
  readonly viewMode = signal<DocumentAnalysisViewMode>('table');
  readonly isLoaded = signal(false);

  readonly filtered = computed(() =>
    this.companies().filter(row => matchesSearch(row, this.search()))
  );
  readonly totals = computed(() => buildTotals(this.companies()));
  readonly isEmpty = computed(() => this.isLoaded() && this.companies().length === 0);
  readonly hasNoSearchResult = computed(
    () => this.isLoaded() && this.companies().length > 0 && this.filtered().length === 0
  );

  constructor(
    store: Store<AppState>,
    translateService: TranslateService,
    private dashboardService: DashboardService,
    private router: Router,
    private toast: ToastHelperService
  ) {
    super(translateService, store);
  }

  ngOnInit(): void {
    this.breadcrumbs = [
      {
        label: this.t(this.tk.Management.DocumentAnalysis),
        routerLink: '/dashboard/document-analysis',
      },
    ];

    this.getDocuments();
  }

  // Carga única da tela: sem loading global por cima, a tabela mostra o esqueleto até `isLoaded`.
  getDocuments(): void {
    this.dashboardService.getDocumentAnalysisTimeline().subscribe({
      next: response => {
        this.companies.set(flattenTimeline(response));
        this.isLoaded.set(true);
      },
      error: error => {
        this.isLoaded.set(true);
        console.error('Error: ', error);

        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingFiles),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  handleSearch(term: string): void {
    this.search.set(term ?? '');
  }

  handleViewMode(mode: DocumentAnalysisViewMode): void {
    this.viewMode.set(mode);
  }

  handleAnalyze(companyId: string): void {
    this.router.navigate(['/dashboard/document-analysis-company', companyId]);
  }
}
